/**
 * Curseur de lecture logique, partagé entre plusieurs GameHistory.
 *
 * Deux tables d'une même donne duplicate n'ont pas forcément le même nombre
 * d'étapes de prélude : CHIEN et ECART n'existent que pour petite/garde. Un
 * index d'étape brut décalerait donc les plis d'une table à l'autre. Le curseur
 * décrit la position en termes logiques et est résolu indépendamment pour
 * chaque historique.
 */

import type { GameHistory, ReplayStep } from './gameHistory';

export type ReplayCursorKind = 'distribution' | 'bids' | 'chien' | 'ecart' | 'trick';

export type ReplayCursor =
  | { kind: 'distribution' | 'bids' | 'chien' | 'ecart' }
  | { kind: 'trick'; trickNumber: number; cardIndex: number };

export interface ReplayPosition {
  stepIndex: number;
  subStep: number;
}

/** Rang canonique d'un type d'étape (les plis viennent toujours en dernier). */
const KIND_RANK: Record<ReplayCursorKind, number> = {
  distribution: 0,
  bids: 1,
  chien: 2,
  ecart: 3,
  trick: 4,
};

/**
 * Les enchères sont encodées comme une seconde étape 'distribution' portant le
 * label 'ENCH' (voir buildGameHistoryFromPartie).
 */
export function stepCursorKind(step: ReplayStep): ReplayCursorKind {
  if (step.type === 'distribution') {
    return step.label === 'ENCH' ? 'bids' : 'distribution';
  }
  return step.type;
}

/** Nombre de cartes navigables dans une étape (0 hors plis). */
export function maxSubStepForStep(history: GameHistory, step: ReplayStep | undefined): number {
  if (!step || step.type !== 'trick' || !step.trickNumber) return 0;
  const trick = history.tricks.find((t) => t.trickNumber === step.trickNumber);
  return trick ? trick.cards.length : 0;
}

export function cursorFromPosition(
  history: GameHistory,
  stepIndex: number,
  subStep: number,
): ReplayCursor {
  const step = history.steps[stepIndex];
  if (!step) return { kind: 'distribution' };
  if (step.type === 'trick' && step.trickNumber) {
    return { kind: 'trick', trickNumber: step.trickNumber, cardIndex: Math.max(0, subStep) };
  }
  return { kind: stepCursorKind(step) as 'distribution' | 'bids' | 'chien' | 'ecart' };
}

/** Dernière étape dont le rang est inférieur ou égal à celui demandé. */
function fallbackIndexForKind(steps: ReplayStep[], kind: ReplayCursorKind): number {
  const targetRank = KIND_RANK[kind];
  let best = 0;
  for (let i = 0; i < steps.length; i++) {
    if (KIND_RANK[stepCursorKind(steps[i])] <= targetRank) best = i;
  }
  return best;
}

/**
 * Résout le curseur pour un historique donné.
 * Une étape absente (ex. CHIEN sur une garde sans) retombe sur l'étape
 * disponible la plus proche en amont.
 */
export function resolveCursor(history: GameHistory, cursor: ReplayCursor): ReplayPosition {
  const { steps } = history;
  if (steps.length === 0) return { stepIndex: 0, subStep: 0 };

  if (cursor.kind !== 'trick') {
    const exact = steps.findIndex((s) => stepCursorKind(s) === cursor.kind);
    const stepIndex = exact >= 0 ? exact : fallbackIndexForKind(steps, cursor.kind);
    return { stepIndex, subStep: 0 };
  }

  const exact = steps.findIndex(
    (s) => s.type === 'trick' && s.trickNumber === cursor.trickNumber,
  );
  if (exact >= 0) {
    return {
      stepIndex: exact,
      subStep: Math.min(Math.max(0, cursor.cardIndex), maxSubStepForStep(history, steps[exact])),
    };
  }

  // Pli inexistant sur cette table (moins de plis joués) : on cale sur le dernier.
  let lastTrickIndex = -1;
  for (let i = steps.length - 1; i >= 0; i--) {
    if (steps[i].type === 'trick') {
      lastTrickIndex = i;
      break;
    }
  }
  if (lastTrickIndex < 0) {
    return { stepIndex: steps.length - 1, subStep: 0 };
  }
  return {
    stepIndex: lastTrickIndex,
    subStep: maxSubStepForStep(history, steps[lastTrickIndex]),
  };
}

/** Curseur correspondant à une progression live (plis terminés + carte en cours). */
export function cursorFromLiveProgress(
  completedTricks: number,
  cardsInCurrentTrick: number,
): ReplayCursor {
  if (completedTricks <= 0 && cardsInCurrentTrick <= 0) {
    return { kind: 'distribution' };
  }
  return {
    kind: 'trick',
    trickNumber: completedTricks + (cardsInCurrentTrick > 0 ? 1 : 0),
    cardIndex: cardsInCurrentTrick > 0 ? cardsInCurrentTrick : Number.MAX_SAFE_INTEGER,
  };
}
